import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useMemberDashboard } from '../hooks/useMemberDashboard';
import { Stack } from '../design-system/components/Layout';
import { spacing } from '../design-system/tokens/spacing';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { CompactIdentityMeta } from '../components/dashboard/CompactIdentityMeta';
import { SubscriptionStatusCard } from '../components/dashboard/SubscriptionStatusCard';
import { OverviewPanel } from '../components/dashboard/OverviewPanel';
import { ProfileCompletionCard } from '../components/dashboard/ProfileCompletionCard';
import { RecentActivityCard } from '../components/dashboard/RecentActivityCard';
import { ExternalNewsWidget } from '../components/dashboard/ExternalNewsWidget';
import { NotificationCard } from '../components/dashboard/NotificationCard';
import { PrimaryNextAction } from '../components/dashboard/PrimaryNextAction';
import { DashboardSkeleton } from '../components/dashboard/DashboardSkeleton';
import { DashboardErrorState } from '../components/dashboard/DashboardErrorState';
import { pickMemberPrimarySource } from '../utils/dashboardPrimaryAction';
import { redirectToXpayCheckout } from '../utils/xpayCheckout';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
  width: 100%;
`;

const MainTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[6]};

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { viewModel, loading, error, reload } = useMemberDashboard();

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const primarySource = useMemo(
    () =>
      viewModel
        ? pickMemberPrimarySource({
            urgentNotices: viewModel.urgentNotices,
            subscription: viewModel.subscription,
            profileCompletion: viewModel.profileCompletion,
          })
        : null,
    [viewModel]
  );

  const primaryAction = useMemo(() => {
    if (!primarySource) return null;
    if (primarySource.kind === 'notice' && primarySource.notice) {
      const n = primarySource.notice;
      return {
        title: n.title,
        message: n.message,
        actionLabel: n.actionLabel ?? t('common.continue'),
        actionLink: n.actionLink ?? '/',
        noticeIndex: primarySource.noticeIndex,
      };
    }
    if (primarySource.kind === 'payment' && primarySource.subscription) {
      const s = primarySource.subscription;
      return {
        title: t('dashboard.subscription.title', { year: s.year }),
        message:
          s.dueDateLabel ||
          s.formattedAmount ||
          t('dashboard.subscription.pending'),
        actionLabel: t('dashboard.subscription.payNow'),
        actionLink: '/payments',
      };
    }
    if (primarySource.kind === 'profile' && primarySource.profileCompletion) {
      const p = primarySource.profileCompletion;
      return {
        title: p.remainingMessage || t('dashboard.profileCompletion.title'),
        message:
          p.missingFieldLabels.slice(0, 3).join(', ') ||
          t('dashboard.profileCompletion.incompleteHint', {
            defaultValue: 'Add the missing details so your membership record stays accurate.',
          }),
        actionLabel: t('dashboard.profileCompletion.completeProfile'),
        actionLink: '/profile/edit',
      };
    }
    return null;
  }, [primarySource, t]);

  if (!user) {
    return <DashboardSkeleton />;
  }

  if (loading && !viewModel) {
    return <DashboardSkeleton />;
  }

  if (error && !viewModel) {
    return <DashboardErrorState onRetry={reload} message={error} />;
  }

  if (!viewModel) {
    return <DashboardErrorState onRetry={reload} />;
  }

  const remainingNotices = viewModel.urgentNotices.filter(
    (_, index) => index !== primaryAction?.noticeIndex
  );

  return (
    <Page>
      <DashboardHeader
        greeting={viewModel.greeting}
        description={viewModel.portalDescription}
        meta={
          <CompactIdentityMeta
            badge={viewModel.identity.membershipBadge}
            profilePath="/profile"
          />
        }
      />

      {primaryAction && (
        <PrimaryNextAction
          title={primaryAction.title}
          message={primaryAction.message ?? ''}
          actionLabel={primaryAction.actionLabel ?? t('common.continue')}
          onAction={async () => {
            if (
              primarySource?.kind === 'payment' &&
              primarySource.subscription?.payment?.id
            ) {
              try {
                await redirectToXpayCheckout(primarySource.subscription.payment.id);
                return;
              } catch (err) {
                console.error('Checkout failed:', err);
                alert(
                  err instanceof Error
                    ? err.message
                    : t('payments.checkoutError')
                );
                return;
              }
            }
            navigate(primaryAction.actionLink);
          }}
        />
      )}

      {remainingNotices.length > 0 && (
        <Stack direction="column" spacing="md">
          {remainingNotices.map((notice, index) => (
            <NotificationCard
              key={index}
              type={notice.type}
              title={notice.title}
              message={notice.message}
              action={
                notice.actionLabel && notice.actionLink
                  ? {
                      label: notice.actionLabel,
                      onClick: () => navigate(notice.actionLink!),
                    }
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      <SubscriptionStatusCard subscription={viewModel.subscription} />

      <OverviewPanel overview={viewModel.overview} />

      <MainTwoCol>
        <Stack direction="column" spacing="md">
          <RecentActivityCard activities={viewModel.activities} />
        </Stack>
        <Stack direction="column" spacing="md">
          <ExternalNewsWidget />
          {viewModel.profileCompletion.percentage < 100 && (
            <ProfileCompletionCard profileCompletion={viewModel.profileCompletion} />
          )}
        </Stack>
      </MainTwoCol>
    </Page>
  );
};

export default Dashboard;
